import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { scanMinimumFormula } from '../utils/scanMinimum';
import ScanMinimumInfo from '../components/ScanMinimumInfo';
import PostpaidMonth from '../components/PostpaidMonth';
import './UsagePage.css';

const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K` : String(n));
const fmtDate = (s, lang) => new Date(s).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
const LEDGER_LIMIT = 20;

export default function UsagePage() {
  const { t, i18n } = useTranslation('settings');
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [pricePer1k, setPricePer1k] = useState(null);
  const [pricePerPage, setPricePerPage] = useState(null);
  const [pricePerWord, setPricePerWord] = useState(null);
  const [pageChars, setPageChars] = useState(null);
  const [customMin, setCustomMin] = useState(10);
  const [customMax, setCustomMax] = useState(1000);
  const [fxRate, setFxRate] = useState(null);
  const [customUsd, setCustomUsd] = useState('');
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);
  const [tab, setTab] = useState('balance');

  const cu = user?.char_usage;

  const loadPacks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getBillingPacks();
      setPacks(data.packs || []);
      setPricePer1k(data.price_per_1k ?? null);
      setPricePerPage(data.price_per_page ?? null);
      setPricePerWord(data.price_per_word ?? null);
      setPageChars(data.page_chars ?? null);
      setCustomMin(data.custom_min_usd ?? 10);
      setCustomMax(data.custom_max_usd ?? 1000);
      setFxRate(data.fx_rate ?? null);
      setError(null);
    } catch (e) {
      setError(t('usage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadLedger = useCallback(async (page) => {
    setLedgerLoading(true);
    try {
      const data = await api.getBillingLedger(page * LEDGER_LIMIT, LEDGER_LIMIT);
      setLedger(data.items || []);
      setLedgerTotal(data.total || 0);
      setLedgerPage(page);
      setLedgerError(null);
    } catch (e) {
      setLedgerError(t('usage.historyError'));
    } finally {
      setLedgerLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Refresh balance (covers returning here after a successful mono payment),
    // then load the current pack prices and the first page of history.
    refreshUser();
    loadPacks();
    loadLedger(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const txLabel = (tx) => {
    if (tx.type === 'debit_translation') return t(`usage.tx.${tx.sub_type || 'translation'}`, t('usage.tx.translation'));
    if (tx.type === 'payg_purchase') return t('usage.tx.purchase');
    if (tx.type === 'refund') return t('usage.tx.refund');
    if (tx.type === 'admin_adjust') return t('usage.tx.adjust');
    if (tx.type === 'free_renewal') return t('usage.tx.renewal');
    return tx.type;
  };

  const handleBuy = async (payload, id) => {
    setBuying(id);
    setError(null);
    try {
      const { invoiceUrl } = await api.checkout(payload);
      window.location.href = invoiceUrl; // hand off to mono checkout
    } catch (e) {
      setError(e.response?.data?.detail || t('usage.checkoutError'));
      setBuying(null);
    }
  };

  const isUnlimited = !cu;
  const freeUsed = cu?.free_used ?? 0;
  const freeLimit = cu?.free_limit ?? 0;
  const usedPct = freeLimit ? Math.min(100, (freeUsed / freeLimit) * 100) : 0;
  const purchasedUsed = cu?.purchased_used ?? 0;
  const purchasedTotal = cu?.purchased_total ?? 0;
  const purchasedPct = purchasedTotal ? Math.min(100, (purchasedUsed / purchasedTotal) * 100) : 0;

  const customAmount = parseFloat(customUsd);
  const customValid = !isNaN(customAmount) && customAmount >= customMin && customAmount <= customMax;
  const customChars = customValid && pricePer1k ? Math.floor((customAmount / pricePer1k) * 1000) : 0;
  const customUah = customValid && fxRate ? Math.round(customAmount * fxRate) : 0;

  if (cu?.postpaid) {
    return (
      <div className="usage-page">
        <header className="usage-header">
          <h1>{t('usage.title')}</h1>
          <p>{t('usage.postpaid.subtitle')}</p>
        </header>
        <PostpaidMonth usage={cu} />
      </div>
    );
  }

  return (
    <div className="usage-page">
      <header className="usage-header">
        <h1>{t('usage.title')}</h1>
        <p>{t('usage.subtitle')}</p>
      </header>

      <div className="usage-tabs">
        <button className={`usage-tab${tab === 'balance' ? ' active' : ''}`} onClick={() => setTab('balance')}>
          {t('usage.tabBalance')}
        </button>
        <button className={`usage-tab${tab === 'history' ? ' active' : ''}`} onClick={() => setTab('history')}>
          {t('usage.history')}
        </button>
      </div>

      {tab === 'balance' && (
      <>
      <section className="usage-balance">
        {isUnlimited ? (
          <div className="usage-stat">
            <span className="num">∞</span>
            <span className="lbl">{t('usage.unlimited')}</span>
          </div>
        ) : (
          <>
            <div className="usage-stat usage-stat-full">
              <span className="num">{fmt(freeUsed)}<span className="den"> / {fmt(freeLimit)}</span></span>
              <span className="lbl">
                {t('usage.freeRemaining')}
                {cu.free_topped_up_to > cu.free_remaining && (
                  <span className="usage-reset">
                    {' · '}{t('usage.resetsIn', { amount: fmt(cu.free_topped_up_to), days: cu.days_until_reset })}
                  </span>
                )}
              </span>
              <div className="usage-bar"><div className="usage-bar-fill" style={{ width: `${usedPct}%` }} /></div>
            </div>
            <div className="usage-stat usage-stat-full">
              <span className="num">{fmt(purchasedUsed)}<span className="den"> / {fmt(purchasedTotal)}</span></span>
              <span className="lbl">{t('usage.purchased')}</span>
              <div className="usage-bar"><div className="usage-bar-fill" style={{ width: `${purchasedPct}%` }} /></div>
            </div>
          </>
        )}
      </section>

      {error && <div className="usage-error">{String(error)}</div>}

      <h2 className="usage-packs-title">{t('usage.buyMore')}</h2>
      {loading ? (
        <div className="usage-loading">{t('usage.loading')}</div>
      ) : (
        <>
          {pricePer1k && (
            <div className="usage-rate">
              {t('usage.rate', {
                per1k: pricePer1k.toFixed(2),
                perPage: pricePerPage?.toFixed(2),
                perWord: pricePerWord?.toFixed(3),
                pageChars: pageChars,
              })}
            </div>
          )}
          <div className="usage-packs">
            {packs.map((p) => (
              <div key={p.key} className={`usage-pack${p.popular ? ' popular' : ''}`}>
                {p.popular && <span className="usage-pack-tag">{t('usage.popular')}</span>}
                <div className="usage-pack-name">{t(`usage.packs.${p.key}`, p.key)}</div>
                <div className="usage-pack-chars">{fmt(p.chars)} {t('usage.chars')}</div>
                {p.uah_price != null ? (
                  <>
                    <div className="usage-pack-price">{p.uah_price} ₴</div>
                    <div className="usage-pack-usd">${p.usd_price}</div>
                  </>
                ) : (
                  <div className="usage-pack-price">${p.usd_price}</div>
                )}
                <button className="usage-pack-btn" disabled={!!buying} onClick={() => handleBuy({ pack_key: p.key }, p.key)}>
                  {buying === p.key ? t('usage.redirecting') : t('usage.buy')}
                </button>
              </div>
            ))}
          </div>

          <div className="usage-custom">
            <div className="usage-custom-label">{t('usage.custom')}</div>
            <div className="usage-custom-row">
              <div className="usage-custom-input">
                <span className="usage-custom-currency">$</span>
                <input
                  type="number"
                  min={customMin}
                  max={customMax}
                  step="1"
                  placeholder={String(customMin)}
                  value={customUsd}
                  onChange={(e) => setCustomUsd(e.target.value)}
                />
              </div>
              <button
                className="usage-pack-btn"
                disabled={!!buying || !customValid}
                onClick={() => handleBuy({ usd_amount: customAmount }, 'custom')}
              >
                {buying === 'custom' ? t('usage.redirecting') : t('usage.buy')}
              </button>
            </div>
            <div className="usage-custom-hint">
              {customValid
                ? (fxRate
                    ? t('usage.customGet', { chars: fmt(customChars), uah: customUah })
                    : t('usage.customGetNoUah', { chars: fmt(customChars) }))
                : t('usage.customRange', { min: customMin, max: customMax })}
            </div>
          </div>
        </>
      )}
      <p className="usage-note">{t('usage.neverExpire')}</p>
      {cu && (
        <p className="usage-note usage-note-small">
          {t('common:scanPageMinimum', { chars: cu.scan_min_chars_per_page.toLocaleString(i18n.language) })}
          <ScanMinimumInfo />
        </p>
      )}
      </>
      )}

      {tab === 'history' && (
      <section className="usage-history">
        <h2 className="usage-packs-title">{t('usage.history')}</h2>
        {ledgerError ? (
          <div className="usage-error">{ledgerError}</div>
        ) : ledger.length === 0 ? (
          <div className="usage-loading">{t('usage.txEmpty')}</div>
        ) : (
          <>
            <table className="usage-table">
              <tbody>
                {ledger.map((tx) => (
                  <tr key={tx.id}>
                    <td className="usage-tx-date">{fmtDate(tx.created_at, i18n.language)}</td>
                    <td className="usage-tx-desc">
                      {txLabel(tx)}
                      {tx.scan_min_chars_per_page != null && (
                        <span className="usage-tx-note">
                          {' · '}
                          {scanMinimumFormula(t, tx, i18n.language)}
                          {cu && <ScanMinimumInfo />}
                        </span>
                      )}
                    </td>
                    <td className={`usage-tx-amt ${tx.chars_delta < 0 ? 'neg' : tx.chars_delta > 0 ? 'pos' : ''}`}>
                      {tx.chars_delta !== 0 ? `${tx.chars_delta > 0 ? '+' : ''}${tx.chars_delta.toLocaleString(i18n.language)}` : '-'}
                    </td>
                    <td className="usage-tx-receipt">
                      {tx.receipt_url && (
                        <a href={tx.receipt_url} target="_blank" rel="noopener noreferrer">{t('usage.tx.receipt')}</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {ledgerTotal > LEDGER_LIMIT && (
              <div className="usage-pager">
                <button disabled={ledgerPage === 0 || ledgerLoading} onClick={() => loadLedger(ledgerPage - 1)}>‹</button>
                <span>{ledgerPage + 1} / {Math.ceil(ledgerTotal / LEDGER_LIMIT)}</span>
                <button
                  disabled={(ledgerPage + 1) * LEDGER_LIMIT >= ledgerTotal || ledgerLoading}
                  onClick={() => loadLedger(ledgerPage + 1)}
                >›</button>
              </div>
            )}
          </>
        )}
      </section>
      )}
    </div>
  );
}
